import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useRef } from 'react';
import { FormDialog, TableCard } from '~/components';
import { useBoardCertRef } from '../account/vaultSelectors';
import { BoardCertForm } from './BoardCertForm';
import { BoardCertsGrid } from './BoardCertsGrid';
import { BoardCertFormValues } from './types';

const TAGS = CT.boardCerts.table;

export const BoardCertDetails = () => {
  const { boardCertRef } = useBoardCertRef();
  if (!boardCertRef) {
    return null;
  }

  const { crudRef, providerCategoryId } = useSelector(boardCertRef, (state) => state.context);

  const {
    form,
    modal,
    loading: crudLoading,
    data: certs,
  } = useSelector(crudRef, (state) => state.context);

  const handleOpen = () => {
    crudRef.send({ type: 'ON_ADD' });
  };

  const handleClose = () => {
    crudRef.send({ type: 'ON_CLOSE_MODAL' });
  };

  const handleCreate = (values: BoardCertFormValues) => {
    crudRef.send({ type: 'ON_SUBMIT', values: values });
  };

  const handleEdit = (values: BoardCertFormValues) => {
    crudRef.send({ type: 'ON_UPDATE', values: values });
  };

  const handleGridDelete = (id: number) => {
    boardCertRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };

  const handleGridEdit = (id: number) => {
    crudRef.send({ type: 'ON_EDIT', payload: { id } });
  };

  // Freeze the modal title during the close transition. Without this, both
  // `modal.create` and `modal.edit` flip false at dismiss time and the title
  // falls through to the "Edit" branch of the ternary — visibly swapping
  // from "Add Board Certificate" to "Edit Board Certificate" mid-animation.
  const lastTitleRef = useRef('Add Board Certificate');
  const isOpen = modal.create || modal.edit;
  const modalTitle = modal.create
    ? 'Add Board Certificate'
    : modal.edit
      ? 'Edit Board Certificate'
      : lastTitleRef.current;
  if (isOpen) {
    lastTitleRef.current = modalTitle;
  }

  return (
    <>
      <TableCard
        mobilePageHeader
        title="Board Certifications"
        addLabel="Add Board Certification"
        onAddClick={handleOpen}
        addBtnTag={TAGS.addBtn}
        layoutTag={CT.boardCerts.layout}
      >
        <div className="h-130">
          <BoardCertsGrid
            handleGridDelete={handleGridDelete}
            handleGridEdit={handleGridEdit}
            data={certs}
            loading={crudLoading}
          />
        </div>
      </TableCard>
      <FormDialog
        title={modalTitle}
        editing={modal.edit}
        creating={modal.create}
        onClose={handleClose}
        padding={'pt-4'}
        bgColor={'white'}
        width={'4xl'}
      >
        <BoardCertForm
          handleClose={handleClose}
          providerCategoryId={providerCategoryId}
          isEdit={modal.edit}
          handleEdit={handleEdit}
          handleCreate={handleCreate}
          form={form}
        />
      </FormDialog>
    </>
  );
};
